import React, { useEffect } from 'react';
import { RouterProvider } from 'react-router-dom';
import { Provider } from 'react-redux';
import { QueryClientProvider } from '@tanstack/react-query';
import { App as AntdApp, ConfigProvider, theme } from 'antd';
import zhCN from 'antd/locale/zh_CN';
import router from './router';
import { store } from './store';
import { initializeAuth, logout, markUnauthenticated } from './store/authSlice';
import {
  AUTH_SESSION_EXPIRED_EVENT,
  AuthSessionExpiredDetail,
  getStoredAuthSession,
} from '@saasplatform/auth';
import ChunkUpdatePrompt from './components/ChunkUpdatePrompt';
import GlobalErrorBoundary from './components/GlobalErrorBoundary';
import 'dayjs/locale/zh-cn';
import { isTenantRuntime } from '@saasplatform/config';
import { tenantQueryClient } from './query/queryClient';

const isProtectedAdminPath = (pathname: string) => {
  if (pathname === '/' || pathname === '/login') {
    return false;
  }

  if (/^\/tenant(\/|$)/.test(pathname)) {
    return false;
  }

  return !isTenantRuntime();
};

const AuthSessionBridge: React.FC = () => {
  useEffect(() => {
    const initializeAdminAuthIfNeeded = (pathname: string) => {
      if (!isProtectedAdminPath(pathname)) {
        return;
      }

      const { initialized, loading } = store.getState().auth;
      if (initialized || loading) {
        return;
      }

      if (!getStoredAuthSession()?.user) {
        store.dispatch(markUnauthenticated());
        return;
      }

      void store.dispatch(initializeAuth());
    };

    initializeAdminAuthIfNeeded(window.location.pathname);

    const unsubscribeRouter = router.subscribe((state) => {
      initializeAdminAuthIfNeeded(state.location.pathname);
    });

    const handleSessionExpired = (event: Event) => {
      const customEvent = event as CustomEvent<AuthSessionExpiredDetail>;
      store.dispatch(logout());
      void router.navigate(customEvent.detail?.tenant ? '/tenant/login' : '/login', {
        replace: true,
      });
    };

    window.addEventListener(AUTH_SESSION_EXPIRED_EVENT, handleSessionExpired as EventListener);
    return () => {
      unsubscribeRouter();
      window.removeEventListener(AUTH_SESSION_EXPIRED_EVENT, handleSessionExpired as EventListener);
    };
  }, []);

  return null;
};

const App: React.FC = () => {
  return (
    <Provider store={store}>
      <QueryClientProvider client={tenantQueryClient}>
        <ConfigProvider
          locale={zhCN}
          theme={{
            algorithm: theme.defaultAlgorithm,
            token: {
              colorPrimary: '#1890ff',
            },
          }}
        >
          <AntdApp>
            <GlobalErrorBoundary>
              <AuthSessionBridge />
              <ChunkUpdatePrompt />
              <RouterProvider router={router} />
            </GlobalErrorBoundary>
          </AntdApp>
        </ConfigProvider>
      </QueryClientProvider>
    </Provider>
  );
};

export default App;
